import React from "react";
import LegalDocumentPage, { type LegalSection } from "./LegalDocumentPage";

const sections: LegalSection[] = [
  {
    heading: "1. Acceptance of Terms",
    paragraphs: [
      "Welcome to T-Accounts. These Terms of Service govern your access to and use of our web-based application that allows users to create and manage T-account visualizations and related accounting diagrams.",
      "By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, you may not use the Service.",
    ],
  },
  {
    heading: "2. Eligibility",
    paragraphs: [
      "You must be at least 18 years old to use the Service.",
      "By using the Service, you represent and warrant that:",
    ],
    bullets: [
      "You have the legal capacity to enter into these Terms.",
      "Your use of the Service will comply with all applicable laws and regulations.",
    ],
  },
  {
    heading: "3. User Accounts",
    paragraphs: [
      "To access certain features of the Service, you may be required to create an account.",
      "You agree to:",
    ],
    bullets: [
      "Provide accurate and complete information.",
      "Maintain the security of your account credentials.",
      "Be responsible for all activities that occur under your account.",
      "Notify us immediately of any unauthorized access to your account.",
      "Understand that accounts that violate these Terms may be suspended or terminated.",
    ],
  },
  {
    heading: "4. Description of the Service",
    paragraphs: [
      "The Service provides tools that allow users to create, store, and manage T-account diagrams and accounting visualizations.",
      "We may modify, suspend, or discontinue any aspect of the Service at any time without notice.",
    ],
  },
  {
    heading: "5. User Content",
    paragraphs: [
      "You retain ownership of the content you create within the Service, including accounting data and diagrams.",
      "By using the Service, you grant us a limited license to store, process, and display your content solely for the purpose of operating and providing the Service.",
      "You are responsible for ensuring that your content does not violate any laws or third-party rights.",
    ],
  },
  {
    heading: "6. Prohibited Uses",
    paragraphs: ["You agree not to use the Service to:"],
    bullets: [
      "Violate any applicable laws or regulations.",
      "Infringe on intellectual property rights.",
      "Upload malicious code or harmful software.",
      "Attempt to gain unauthorized access to systems or accounts.",
      "Interfere with the operation or security of the Service.",
      "Use the Service for fraudulent or deceptive purposes.",
    ],
  },
  {
    heading: "7. Payments and Subscriptions",
    paragraphs: [
      "Certain features of the Service may require a paid subscription.",
      "If you purchase a subscription:",
    ],
    bullets: [
      "Payments will be processed through a third-party payment provider, such as Stripe.",
      "Subscription fees will be billed according to the plan you select.",
      "Subscriptions may automatically renew unless canceled.",
      "You are responsible for managing your subscription and canceling if you no longer wish to continue.",
      "We reserve the right to change pricing or subscription terms with reasonable notice.",
    ],
  },
  {
    heading: "8. Intellectual Property",
    paragraphs: [
      "The Service, including its software, design, branding, and content (excluding user-generated content), is owned by JPWare LLC and protected by intellectual property laws.",
      "You may not copy, modify, reverse engineer, redistribute, or sell any part of the Service without our prior written permission.",
    ],
  },
  {
    heading: "9. Service Availability",
    paragraphs: [
      "We strive to maintain reliable service, but we do not guarantee that the Service will be uninterrupted or error-free.",
      "The Service may be unavailable due to maintenance, system updates, technical failures, or circumstances beyond our control.",
    ],
  },
  {
    heading: "10. Disclaimer of Warranties",
    paragraphs: [
      "The Service is provided as is and as available.",
      "To the fullest extent permitted by law, we disclaim all warranties, including merchantability, fitness for a particular purpose, non-infringement, and accuracy or reliability of results.",
      "The Service is intended as a visualization tool and does not constitute professional accounting or financial advice.",
    ],
  },
  {
    heading: "11. Limitation of Liability",
    paragraphs: [
      "To the maximum extent permitted by law, JPWare LLC shall not be liable for any indirect, incidental, special, or consequential damages arising from use of the Service, inability to use the Service, loss of data, or business interruption.",
      "Our total liability shall not exceed the amount paid by you for the Service in the 12 months preceding the claim.",
    ],
  },
  {
    heading: "12. Termination",
    paragraphs: [
      "We may suspend or terminate your access to the Service at any time if you violate these Terms.",
      "You may terminate your account at any time by discontinuing use of the Service or requesting account deletion.",
      "Upon termination, your right to use the Service will immediately cease, and we may delete or retain your data according to our Privacy Policy.",
    ],
  },
  {
    heading: "13. Governing Law",
    paragraphs: [
      "These Terms shall be governed by and interpreted under the laws of Florida, USA, without regard to conflict of law principles.",
      "Any disputes arising from these Terms shall be resolved in the courts located within Florida, USA.",
    ],
  },
  {
    heading: "14. Changes to These Terms",
    paragraphs: [
      "We may update these Terms from time to time.",
      "When changes occur, we will update the Last Updated date and may notify users through the Service.",
      "Continued use of the Service after changes indicates acceptance of the revised Terms.",
    ],
  },
  {
    heading: "15. Contact Information",
    bullets: ["Email: support@t-accounts.com", "Company: JPWare LLC"],
  },
];

export default function TermsOfUsePage() {
  return <LegalDocumentPage title="Terms of Use" lastUpdated="Mar. 10, 2026" sections={sections} />;
}